"use client"

// Room Display Mapping Hooks
// Provides React hooks for transforming room types, themes, and dimensions to display values

import { useMemo } from "react"
import {
  transformRoomTypeValue,
  transformRoomThemeValue,
  transformRoomDimensionValue,
  logUnmappedValue,
} from "../src/utils/roomValueTransformations"
import { ROOM_TYPE_MAPPINGS, ROOM_THEME_MAPPINGS } from "../src/utils/api-value-mappings"

/**
 * Hook to transform room type API value to display name
 * @param rawRoomType - Raw room type value from API
 * @returns Display-friendly room type name
 */
export function useDisplayRoomType(rawRoomType: string): string {
  return useMemo(() => {
    if (!rawRoomType) return ""

    const transformed = transformRoomTypeValue(rawRoomType)

    // Log unmapped values in development
    const isDirectMapping = ROOM_TYPE_MAPPINGS.some((mapping) => mapping.value === rawRoomType)
    if (!isDirectMapping && rawRoomType !== transformed) {
      logUnmappedValue("roomType", rawRoomType, transformed)
    }

    return transformed
  }, [rawRoomType])
}

/**
 * Hook to transform room theme API value to display name
 * @param rawRoomTheme - Raw room theme value from API
 * @returns Display-friendly room theme name
 */
export function useDisplayRoomTheme(rawRoomTheme: string): string {
  return useMemo(() => {
    if (!rawRoomTheme) return ""

    const transformed = transformRoomThemeValue(rawRoomTheme)

    // Log unmapped values in development
    const isDirectMapping = ROOM_THEME_MAPPINGS.some((mapping) => mapping.value === rawRoomTheme)
    if (!isDirectMapping && rawRoomTheme !== transformed) {
      logUnmappedValue("roomTheme", rawRoomTheme, transformed)
    }

    return transformed
  }, [rawRoomTheme])
}

/**
 * Hook to transform room dimension API value to display name
 * @param rawRoomDimension - Raw room dimension value from API
 * @returns Display-friendly room dimension name
 */
export function useDisplayRoomDimension(rawRoomDimension: string): string {
  return useMemo(() => {
    if (!rawRoomDimension) return ""

    const transformed = transformRoomDimensionValue(rawRoomDimension)

    // Log transformation in development
    if (process.env.NODE_ENV === "development" && rawRoomDimension !== transformed) {
      console.warn(`[Room Mapping] Dimension transformed: "${rawRoomDimension}" → "${transformed}"`)
    }

    return transformed
  }, [rawRoomDimension])
}

/**
 * Utility functions for non-hook contexts (e.g., utility functions, server components)
 */
export const getDisplayRoomType = transformRoomTypeValue
export const getDisplayRoomTheme = transformRoomThemeValue
export const getDisplayRoomDimension = transformRoomDimensionValue
